import { TranslateService } from '@ngx-translate/core';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { concatMap } from 'rxjs/operators';

import { DirectoryUtilsService } from '../../services/directory-utils/directory-utils.service';
import { DirectoryModificationsRequestService } from '../../services/directory-modifications-request/directory-modifications-request.service';
import { DirectorySearchService } from './../../services/directory-search/directory-search.service';
import { DirectorySharedDataService } from 'src/app/directory/services/directory-shared-data.service';

import { ModificationsValue, Requester } from './../../models/directory-modifications-request.model';
import { DirectoryConfig, DirectoryFieldForFilter } from '../../models/directory-config.model';
import { DirectoryModificationsRequestValidationComponent } from '../../components/dialog/directory-modifications-request-validation/directory-modifications-request-validation.component';
import { DirectoryAskLeaveDialogComponent } from '../../components/dialog/directory-ask-leave-dialog/directory-ask-leave-dialog.component';
import { Subscription } from 'rxjs';
import { DirectoryEmbedRequestService } from '../../services/directory-embed-request/directory-embed-request.service';
import { DirectoryResultsStoreService } from '../../services/directory-results-store/directory-results-store.service';
import { OpenedElement } from '../../models/directory-opened-element.model';

@Component({
  selector: 'app-directory-entity-page',
  templateUrl: './directory-entity-page.component.html',
  styleUrls: ['./directory-entity-page.component.scss'],
})
export class DirectoryEntityPageComponent implements OnInit, OnDestroy {
  public currentOpenedEntityId: string;
  public originalEntityId: string;
  public widgetConfig: DirectoryConfig;
  public tmpWidgetConfig: DirectoryConfig;
  public modificationsRequests: ModificationsValue[] = [];
  public openedElementRange: string;
  public availableFields: any[];
  public fieldsSubscription: Subscription;
  public resultsStoreSubscription: Subscription;
  public isSecondaryItem: boolean = false;

  constructor (
    private location: Location,
    private route: ActivatedRoute,
    public directoryUtils: DirectoryUtilsService,
    private directoryData: DirectorySharedDataService,
    private directorySearch: DirectorySearchService,
    private snackbar: MatSnackBar,
    private translate: TranslateService,
    public directoryModificationsRequest: DirectoryModificationsRequestService,
    private directoryEmbedRequestService: DirectoryEmbedRequestService,
    private router: Router,
    private dialog: MatDialog,
    private resultsStore: DirectoryResultsStoreService,
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.get('editMode') === 'modifications') {
      this.directoryModificationsRequest.isEditionModeActive = true;
    }
    this.directoryData.widgetId = this.route.snapshot.paramMap.get('widgetId');

    this.translate.use(this.route.snapshot.paramMap.get('lang'));
    this.directoryData.widgetLang = this.route.snapshot.paramMap.get('lang');
    this.directorySearch.widgetLang = this.route.snapshot.paramMap.get('lang');

    // subscription to find & complete all available fields and set entity ID and range first values
    this.fieldsSubscription = this.directoryData.findDirectoryConfig(this.route.snapshot.paramMap.get('widgetId')).pipe(
      concatMap((config: DirectoryConfig) => {
        this.directoryUtils.isHostAuthorized(config.authorizedHosts);
        this.directoryData.setHeaders(config.apiKey);
        this.directorySearch.setHeaders(config.apiKey);
        this.directorySearch.setConcernedSubject(config.isFocusOnAssociatedDB, config.concernedAssociatedDB);
        this.directoryUtils.setSheet(config.addedStyleLink);
        //temporary add future way to deal with headers fields
        this.setHeaderFieldsLabelsforEdit(config);

        if(!this.directoryData.isInitialized()) this.directoryData.setAvailableFields();
        this.tmpWidgetConfig = config;
        // this.directoryData.changeWidgetConfig(config);
        this.directoryData.concernedAssociatedDB = config.concernedAssociatedDB;

        // Finding the element range
        // This needs to be here because is uses 'config'
        if(this.route.snapshot.paramMap.get('domain')) { // Second level. If it's 'null', it's an Entity
          this.openedElementRange = (this.route.snapshot.paramMap.get('domain') == 'null' ? null : this.route.snapshot.paramMap.get('domain'));
          if (this.tmpWidgetConfig.layout.onlySpecificFields) this.isSecondaryItem = true;
        } else {
          this.openedElementRange = config.concernedAssociatedDB; // First level . It comes from the widget configuration.
        }
        this.directoryModificationsRequest.currentElementDomain = this.openedElementRange;

        // Finding the element ID - Does this need to be in this subscription?
        this.originalEntityId = this.route.snapshot.paramMap.get('entityId');
        this.currentOpenedEntityId = this.originalEntityId;

        // Reroute when add requests are forced
        if(config.addRequests && config.addRequests.forceAddRequests && !this.route.snapshot.url.map(x => x.path).includes("demande-ajout")) {
          this.router.navigateByUrl(
            `/${this.route.snapshot.paramMap.get('lang')}/annuaire/${this.route.snapshot.paramMap.get('widgetId')}/demande-ajout`
            );
        }

        return this.directoryData.getAvailableFields();
      })
    ).subscribe((fields) => {
      //console.log("FieldsEntityPage",fields)
      // Complete the available fields data
      if(fields && !this.directoryData.isInitialized()) {
        this.availableFields = fields;
        this.addPropertiesToFields();
        this.widgetConfig = this.tmpWidgetConfig;
        //console.log("widgetConfigEntityPage-Change",this.widgetConfig)
        this.directoryData.changeWidgetConfig(this.widgetConfig);
      } else if (fields && this.directoryData.isInitialized()){
        this.availableFields = fields;
        this.addPropertiesToFields();
        this.widgetConfig = this.tmpWidgetConfig;
      }
    });

    // Subscribe to the resultsStore so focus on element/entity can be changed when clickinkg a link
    // (this is inactive at the moment as this feature is not required yet)
    // this.resultsStoreSubscription = this.resultsStore.getOpenedElement().subscribe((openedElement: OpenedElement) => {
    //   console.debug('Link click is asking to move to another element', openedElement);

    //   if (openedElement && openedElement.isMain) {
    //     this.currentOpenedEntityId = openedElement.id;
    //     this.openedElementRange = openedElement.range;
    //   } else {
    //     this.openedElementRange = null;
    //     this.currentOpenedEntityId = null;
    //   }
    // });

    this.directoryModificationsRequest.getModificationsRequests().
      subscribe((modificationsRequests: ModificationsValue[]) => {
        this.modificationsRequests = modificationsRequests;
    });
  }

  public addPropertiesToFields() {
    this.tmpWidgetConfig.sheet.fieldsgroup.forEach((group) =>
      group.fields.forEach((field) => {
        const dynamicField = this.availableFields.find((x) =>
          x.field_id === field.id
        );
        field.domain = dynamicField?.associated_data_domain_id;
        field.range = dynamicField?.associated_data_id;
        field.readOnly = dynamicField?.readonly;
        field.isExternal = dynamicField?.isExternal;
        field.associated_data_id = dynamicField?.associated_data_id;
        field.ancestry = dynamicField?.ancestry;
        field.interestAreaRestriction = dynamicField?.accessible_interest_areas;
        field.communityRestriction = dynamicField?.accessible_communities;
        field.interestArea = dynamicField?.interest_area?.interest_area_id;
        if (dynamicField != undefined) {
          const fieldCommunities = [];
          dynamicField.communities.forEach(community => {
            fieldCommunities.push(community.community_id);
          });
          field.communities = fieldCommunities;
        }
      }));

      this.tmpWidgetConfig?.secondarySheets?.forEach(sheet => {
        sheet?.fieldsgroup?.forEach((group) =>
            group.fields.forEach((field) => {
              const dynamicField = this.availableFields.find((x) =>
                x.field_id === field.id
              );
              field.domain = dynamicField?.associated_data_domain_id;
              field.range = dynamicField?.associated_data_id;
              field.readOnly = dynamicField?.readonly;
              field.isExternal = dynamicField?.isExternal;
              field.ancestry = dynamicField?.ancestry;
              field.interestAreaRestriction = dynamicField?.accessible_interest_areas;
              field.communityRestriction = dynamicField?.accessible_communities;
              field.interestArea = dynamicField?.interest_area?.interest_area_id;
              if (dynamicField != undefined) {
                const fieldCommunities = [];
                dynamicField.communities.forEach(community => {
                  fieldCommunities.push(community.community_id);
                });
                field.communities = fieldCommunities;
              }
          }));
      });
      this.tmpWidgetConfig.filters.list.forEach((filter: DirectoryFieldForFilter) => {
        if (filter.type == 'LinkedChoice') {
          // update filter properties to be more dynamic
          const dynamicField = this.availableFields.find((x) =>
            x.field_id === filter.id
          );
          filter.range = dynamicField?.associated_data_id
          filter.associated_data_id = dynamicField?.associated_data_id
          filter.ancestry = dynamicField?.ancestry
        }
      });

  }

  public setHeaderFieldsLabelsforEdit(config) {

    if(!config.concernedAssociatedDB || config.concernedAssociatedDB == "") {
      config.concernedAssociatedDB = null;
    }

    if(config?.sheet?.description?.id) {
      config.sheet.description.domain = config.concernedAssociatedDB;
      config.sheet.description['overrideTitle'] = {'fr':"Description",'en':"Description"};
      config.sheet.description['showfieldTitle'] = true;
    }
    if(config?.sheet?.banner?.id) {
      config.sheet.banner.domain = config.concernedAssociatedDB;
      config.sheet.banner['overrideTitle'] = {'fr':"Bannière",'en':"Banner"};
      config.sheet.banner['showfieldTitle'] = true;
    }
    if(config?.sheet?.logo?.id) {
      config.sheet.logo.domain = config.concernedAssociatedDB;
      config.sheet.logo['overrideTitle'] = {'fr':"Logo",'en':"Logo"};
      config.sheet.logo['showfieldTitle'] = true;
    }

    if(config?.sheet?.title?.id) {
      config.sheet.title.domain = config.concernedAssociatedDB;
    }

    if (config?.secondarySheets) {
      config.secondarySheets.forEach( sheet => {
        if (sheet.classId != 'entityForm') {
          sheet.title.domain = sheet.classId;
          sheet.logo.domain = sheet.classId;
          sheet.description.domain = sheet.classId;
          sheet.banner.domain = sheet.classId;
        } // else ? put null
        if(sheet?.description?.id) {
          sheet.description['overrideTitle'] = {'fr':"Description",'en':"Description"};
          sheet.description['showfieldTitle'] = true;
        }
        if(sheet?.banner?.id) {
          sheet.banner['overrideTitle'] = {'fr':"Bannière",'en':"Banner"};
          sheet.banner['showfieldTitle'] = true;
        }
        if(sheet?.logo?.id) {
          sheet.logo['overrideTitle'] = {'fr':"Logo",'en':"Logo"};
          sheet.logo['showfieldTitle'] = true;
        }
      })
    }
  }

  ngOnDestroy(): void {
    if (this.fieldsSubscription) this.fieldsSubscription.unsubscribe();
    if (this.resultsStoreSubscription) this.resultsStoreSubscription.unsubscribe();
    // directoryModificationsRequest too ?
  }

  public goBack() {
    console.log("okok")
    this.directoryModificationsRequest.destroyStore();
    this.location.back();
  }

  public askGoBack() {
    if (this.modificationsRequests.length == 0) {
      this.goBack();
    } else {
      const dialogConfirm = this.dialog.open(DirectoryAskLeaveDialogComponent);
      dialogConfirm.afterClosed().subscribe( leave => {
        if (leave) {
          this.directoryEmbedRequestService.newObjectsRequest = {};
          this.goBack();
        }
      });
    }
  }

  public openRequestValidationDialog() {
    this.openRequestValidationDialogWithParam(
      this.modificationsRequests
    ).then(
      (requester: Requester) => {
        if (requester !== undefined && requester !== null) {
          this.directoryModificationsRequest.requester = requester;
          this.directoryModificationsRequest.sendRequest(requester, this.currentOpenedEntityId).subscribe(
            res => {
              this.snackbar.open(this.translate.instant("directory.requests.sent"), "OK");
              this.goBack();
            }
          );
        }
      }
    );
  }

  public openRequestValidationDialogWithParam(
    modifications: ModificationsValue[]
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dialogRef = this.dialog.open(
        DirectoryModificationsRequestValidationComponent,
        {
          width: '80vw',
          maxWidth: '100vw',
          panelClass: 'custom-dialog-field-request-container',
          data: modifications,
        }
      );
      dialogRef.afterClosed().subscribe((requester: Requester) => {
        resolve(requester);
      });
    });
  }
}
